// src/MainMenu.js
import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  parseSinglePuzzleSearchParams,
  parseEndlessSearchParams,
} from './puzzleParams';
import GraphAnimator from './GraphAnimator';
import GOLTitleBackground from './GOLTitleBackground';
import { useAuth } from './AuthContext';
import { apiFetch } from './api';
import { buildSystemPrompt, estimateTokens, lookupPrice } from './llmService';
import { LLM_PROVIDERS, API_KEYS_STORAGE_KEY, loadApiKeys, saveApiKeys } from './llmProviders';
import { LIVE_TIME_CONTROLS, TIME_CONTROL_LABELS, TIME_CONTROL_SHORT_LABELS } from './timeControls';
import './MainMenu.css';

// Memoized graph animator - won't re-render when parent state changes
const MemoizedGraphAnimator = memo(() => (
  <GraphAnimator
    nNodes={121}
    rule="standard"
    layout="circle"
    mode="pingpong"
    particleSpeed={[13, 13]}
    showNodes={false}
    showEdges={false}
    palette={[
      '#61f2ff', // cyan highlight
      '#f4b2ff', // pink/magenta
      '#e2e6f3', // off-white
    ]}
  />
));

MemoizedGraphAnimator.displayName = 'MemoizedGraphAnimator';

// API key page URLs for each provider
const API_KEY_URLS = {
  Google: 'https://aistudio.google.com/app/apikey',
  Anthropic: 'https://console.anthropic.com/settings/keys',
  OpenAI: 'https://platform.openai.com/api-keys',
  XAI: 'https://console.x.ai/',
  OpenRouter: 'https://openrouter.ai/settings/keys',
};


// Clear all stored API keys
const clearApiKeys = () => {
  try {
    sessionStorage.removeItem(API_KEYS_STORAGE_KEY);
  } catch {
    console.warn('Failed to clear API keys');
  }
};

const ALPHAZERO_SUPPORTED_SIZES = [[21, 15]];   // the deployed model was trained to 21x15
const DEFAULT_BOARD_SIZE = { rows: 21, cols: 15 };

const MENU_SCENES = {
  MAIN: 'main',
  TUTORIAL: 'tutorial',
  VS_COMPUTER: 'vs_computer',
  LOCAL_MULTIPLAYER: 'local_multiplayer',
  ONLINE_MULTIPLAYER: 'online_multiplayer',
  PUZZLE_MODE_SELECT: 'puzzle_mode_select',
  PUZZLE: 'puzzle',
  ENDLESS_PUZZLE_CONFIG: 'endless_puzzle_config',
  PRICING: 'pricing',
  ACCOUNT: 'account',
  LEADERBOARD: 'leaderboard',
};

/**
 * Button component with hover/selected states
 */
const MenuButton = ({ 
  text, 
  onClick, 
  isSelected = false, 
  isActive = false, // For toggle state (ON/OFF)
  disabled = false,
  variant = 'default',
  className = '',
  onMouseEnter,
  usingKeyboard = false,
  animationKey = 0,
}) => {
  const classes = [
    'menu-button',
    `menu-button--${variant}`,
    isSelected ? 'menu-button--selected' : '',
    isActive ? 'menu-button--active' : '',
    disabled ? 'menu-button--disabled' : '',
    usingKeyboard ? 'menu-button--keyboard-nav' : '',
    className,
  ].filter(Boolean).join(' ');
  
  return (
    <button
      className={classes}
      onClick={disabled ? undefined : (e) => { onClick?.(e); e.currentTarget.blur(); }}
      onMouseEnter={onMouseEnter}
      disabled={disabled}
      key={isSelected ? `selected-${animationKey}` : 'not-selected'}
    >
      <span className="menu-button__sweep" key={isSelected ? animationKey : 'idle'} />
      <span className="menu-button__text">{text}</span>
    </button>
  );
};

/**
 * Badge component for displaying values
 */
const Badge = ({ text, className = '' }) => (
  <div className={`menu-badge ${className}`}>
    {text}
  </div>
);

/**
 * Editable Badge component - click to edit value directly
 */
const EditableBadge = ({ 
  label, 
  value, 
  onChange, 
  min = 1, 
  max = 99, 
  ensureOdd = false,
  isSelected = false,
  onMouseEnter,
  usingKeyboard = false,
  forceEdit = false, // External trigger to start editing
  onEditingChange, // Callback when editing state changes
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(String(value));
  const inputRef = useRef(null);
  
  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);
  
  // Respond to external edit trigger
  useEffect(() => {
    if (forceEdit && !isEditing) {
      startEditing();
    }
  }, [forceEdit]);
  
  // Update editValue when value prop changes (from arrow buttons)
  useEffect(() => {
    if (!isEditing) {
      setEditValue(String(value));
    }
  }, [value, isEditing]);
  
  const startEditing = () => {
    setIsEditing(true);
    setEditValue(String(value));
    onEditingChange?.(true);
  };
  
  const finishEditing = () => {
    let newValue = parseInt(editValue, 10);
    
    // Validate
    if (isNaN(newValue)) {
      newValue = value; // Keep old value if invalid
    } else {
      // Clamp to range
      newValue = Math.max(min, Math.min(max, newValue));
      
      // Ensure odd if required
      if (ensureOdd && newValue % 2 === 0) {
        newValue = newValue + 1;
        if (newValue > max) newValue = newValue - 2;
      }
    }
    
    setEditValue(String(newValue));
    onChange(newValue);
    setIsEditing(false);
    onEditingChange?.(false);
  };
  
  const cancelEditing = () => {
    setEditValue(String(value));
    setIsEditing(false);
    onEditingChange?.(false);
  };
  
  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      finishEditing();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      cancelEditing();
    }
    // Stop all key events from bubbling when editing
    e.stopPropagation();
  };
  
  const classes = [
    'menu-badge',
    'menu-badge--editable',
    isSelected ? 'menu-badge--selected' : '',
    isEditing ? 'menu-badge--editing' : '',
    usingKeyboard ? 'menu-badge--keyboard-nav' : '',
  ].filter(Boolean).join(' ');
  
  if (isEditing) {
    return (
      <div className={classes}>
        <span className="menu-badge__label">{label}: </span>
        <input
          ref={inputRef}
          type="number"
          className="menu-badge__input"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={finishEditing}
          onKeyDown={handleInputKeyDown}
          min={min}
          max={max}
        />
      </div>
    );
  }
  
  return (
    <div 
      className={classes}
      onClick={startEditing}
      onMouseEnter={onMouseEnter}
    >
      {label}: {value}
    </div>
  );
};

/**
 * Arrow selector for numeric values
 */
const ArrowSelector = ({ label, value, onPrev, onNext, min, max, showRange = false }) => {
  // Without the range caption we render flat children so existing CSS for
  // .arrow-selector (horizontal flex row) still applies — and inline call
  // sites in other config screens that hand-roll .arrow-selector keep
  // working unchanged. With the range we wrap children in a row + caption
  // and add the --with-range modifier class.
  const buttons = [
    <MenuButton key="prev" text="‹" onClick={onPrev} disabled={value <= min} variant="arrow" />,
    <Badge key="badge" text={`${label}: ${value}`} />,
    <MenuButton key="next" text="›" onClick={onNext} disabled={value >= max} variant="arrow" />,
  ];
  if (!showRange) {
    return <div className="arrow-selector">{buttons}</div>;
  }
  return (
    <div className="arrow-selector arrow-selector--with-range">
      <div className="arrow-selector__row">{buttons}</div>
      <span className="arrow-selector__range">{min}–{max}</span>
    </div>
  );
};

/**
 * Dropdown selector for providers/models
 */
const DropdownSelector = ({
  label,
  value,
  options,
  onChange,
  disabled = false,
  disabledOptions = [],
  isSelected = false,
  onMouseEnter,
  usingKeyboard = false,
  onOpenChange, // Callback to notify parent when dropdown opens/closes
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef(null);
  
  const classes = [
    'dropdown-selector',
    isSelected ? 'dropdown-selector--selected' : '',
    usingKeyboard ? 'dropdown-selector--keyboard-nav' : '',
    isOpen ? 'dropdown-selector--open' : '',
  ].filter(Boolean).join(' ');
  
  // Handle opening/closing and arrow navigation
  const handleKeyDown = (e) => {
    if (!isSelected || disabled) return;
    
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      
      if (!isOpen) {
        // Open dropdown
        setIsOpen(true);
        onOpenChange?.(true);
      } else {
        // Close dropdown (confirm selection)
        setIsOpen(false);
        onOpenChange?.(false);
      }
    } else if (isOpen && (e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'w' || e.key === 's')) {
      e.preventDefault();
      e.stopPropagation();
      
      const currentIdx = options.indexOf(value);
      let newIdx = currentIdx;
      const dir = (e.key === 'ArrowUp' || e.key === 'w') ? -1 : 1;

      for (let i = currentIdx + dir; i >= 0 && i < options.length; i += dir) {
        if (!disabledOptions.includes(options[i])) {
          newIdx = i;
          break;
        }
      }

      if (newIdx !== currentIdx) {
        onChange(options[newIdx]);
      }
    } else if (isOpen && e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      setIsOpen(false);
      onOpenChange?.(false);
    }
  };
  
  // Close dropdown when deselected (only during keyboard navigation)
  useEffect(() => {
    if (usingKeyboard && !isSelected && isOpen) {
      setIsOpen(false);
      onOpenChange?.(false);
    }
  }, [isSelected, isOpen, onOpenChange, usingKeyboard]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e) => {
      if (selectRef.current && !selectRef.current.contains(e.target)) {
        setIsOpen(false);
        onOpenChange?.(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onOpenChange]);

  // Attach keyboard handler
  useEffect(() => {
    if (isSelected && usingKeyboard) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [isSelected, usingKeyboard, isOpen, value, options]);
  
  return (
    <div ref={selectRef} className={classes} onMouseEnter={onMouseEnter}>
      <span className="dropdown-label">{label}</span>
      <div 
        className="dropdown-display"
        onClick={() => {
          if (disabled) return;
          const newOpen = !isOpen;
          setIsOpen(newOpen);
          onOpenChange?.(newOpen);
        }}
      >
        <span className="dropdown-value">{value}</span>
        <span className="dropdown-arrow">{isOpen ? '▲' : '▼'}</span>
      </div>
      {isOpen && (
        <div className="dropdown-options">
          {options.map((opt, idx) => {
            const isDisabled = disabledOptions.includes(opt);
            return (
              <div
                key={opt}
                className={`dropdown-option ${opt === value ? 'dropdown-option--selected' : ''} ${isDisabled ? 'dropdown-option--disabled' : ''}`}
                onClick={(e) => {
                  if (isDisabled) return;
                  onChange(opt);
                  setIsOpen(false);
                  onOpenChange?.(false);
                  e.currentTarget.blur();
                }}
              >
                {opt}{isDisabled ? ' (coming soon)' : ''}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/**
 * Main Menu Scene
 */
const MainMenuContent = ({ onNavigate, onStartTutorial, selectedIndex, setSelectedIndex, savedSession, onResumeGame, onDiscardSession, onRejoinOnlineGame }) => {
  const [usingKeyboard, setUsingKeyboard] = useState(false);
  const [animationKey, setAnimationKey] = useState(0);
  const { user, tier, token, login, logout } = useAuth();

  // Watch for a still-active online game the player can rejoin within the
  // server's disconnect grace window. Re-render every second so the
  // countdown ticks down visibly and the button auto-disappears at zero.
  const [pendingOnlineGame, setPendingOnlineGame] = useState(() => {
    try {
      const raw = sessionStorage.getItem('phutball_pending_online_game');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed?.gameId || !parsed?.leftAt) return null;
      return parsed;
    } catch { return null; }
  });
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    if (!pendingOnlineGame) return;
    const id = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, [pendingOnlineGame]);
  const rejoinRemainingMs = pendingOnlineGame
    ? Math.max(0, (pendingOnlineGame.leftAt + (pendingOnlineGame.graceMs || 60_000)) - nowTick)
    : 0;
  useEffect(() => {
    if (pendingOnlineGame && rejoinRemainingMs <= 0) {
      try { sessionStorage.removeItem('phutball_pending_online_game'); } catch {}
      setPendingOnlineGame(null);
    }
  }, [rejoinRemainingMs, pendingOnlineGame]);

  // Google Sign-In handler
  const handleGoogleSignIn = useCallback(async () => {
    // Load Google Identity Services if not already loaded
    if (!window.google?.accounts?.id) {
      console.warn('Google Identity Services not loaded');
      return;
    }
    window.google.accounts.id.prompt((notification) => {
      if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
        console.warn('Google sign-in prompt not shown:', notification.getNotDisplayedReason?.() || notification.getSkippedReason?.());
      }
    });
  }, []);

  // Initialize Google Sign-In on mount
  useEffect(() => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId || !window.google?.accounts?.id) return;

    window.google.accounts.id.initialize({
      client_id: clientId,
      callback: async (response) => {
        try {
          await login(response.credential);
        } catch (e) {
          console.error('Google sign-in failed:', e);
        }
      },
    });
  }, [login]);

  // Build button list dynamically based on whether there's a saved session
  const buttons = [];

  const handleDiscardPendingOnline = useCallback(() => {
    const pending = pendingOnlineGame;
    try { sessionStorage.removeItem('phutball_pending_online_game'); } catch {}
    setPendingOnlineGame(null);
    // Resign through a short-lived WS so the opponent (who is still in the
    // game waiting on our grace window) gets a normal game_over and is
    // released from the room instead of having to wait for the timeout.
    if (pending?.gameId && token) {
      try {
        const apiUrl = process.env.REACT_APP_API_URL || '';
        const wsProtocol = apiUrl.startsWith('https') ? 'wss' : 'ws';
        const wsHost = apiUrl.replace(/^https?:\/\//, '') || window.location.host;
        const wsUrl = `${wsProtocol}://${wsHost}/game/${pending.gameId}?token=${encodeURIComponent(token)}`;
        const ws = new WebSocket(wsUrl);
        ws.onopen = () => {
          try { ws.send(JSON.stringify({ action: 'resign' })); } catch {}
          // Give the server a tick to broadcast game_over before closing.
          setTimeout(() => { try { ws.close(); } catch {} }, 250);
        };
        ws.onerror = () => { try { ws.close(); } catch {} };
      } catch {}
    }
  }, [pendingOnlineGame, token]);

  if (pendingOnlineGame && rejoinRemainingMs > 0) {
    const seconds = Math.ceil(rejoinRemainingMs / 1000);
    buttons.push({
      text: `Rejoin Game vs ${pendingOnlineGame.opponentName || 'opponent'} (${seconds}s)`,
      action: () => {
        if (onRejoinOnlineGame) onRejoinOnlineGame(pendingOnlineGame);
      },
      enabled: true,
      variant: 'resume',
      onDiscard: handleDiscardPendingOnline,
    });
  }

  if (savedSession) {
    const sessionConfig = savedSession.config || {};
    const opponent = sessionConfig.p1 === 'Human' ? sessionConfig.p2 : sessionConfig.p1;
    buttons.push({
      text: `Resume Game${opponent ? ` vs ${opponent}` : ''}`,
      action: onResumeGame,
      enabled: true,
      variant: 'resume',
    });
  }

  buttons.push(
    { text: 'Play vs Computer', action: () => onNavigate(MENU_SCENES.VS_COMPUTER), enabled: true },
    { text: 'Play Local Multiplayer', action: () => onNavigate(MENU_SCENES.LOCAL_MULTIPLAYER), enabled: true },
    {
      text: 'Play Online',
      // Starting a different online game implicitly abandons the pending
      // rejoin offer: clear it locally and resign server-side so the
      // opponent isn't left waiting out the disconnect grace window.
      action: () => {
        if (pendingOnlineGame && rejoinRemainingMs > 0) {
          handleDiscardPendingOnline();
        }
        onNavigate(MENU_SCENES.ONLINE_MULTIPLAYER);
      },
      enabled: true,
    },
    { text: 'Quick Start', action: () => onStartTutorial('genesis'), enabled: true },
    { text: 'Full Tutorial', action: () => onStartTutorial('full'), enabled: true },
    { text: 'Puzzles (dev)', action: () => onNavigate(MENU_SCENES.PUZZLE_MODE_SELECT), enabled: true, variant: 'secondary' },
  );

  // TODO: Uncomment for paid tier launch (late May)
  // if (user && tier !== 'free') {
  //   buttons.push({ text: `Account (${tier})`, action: () => onNavigate(MENU_SCENES.ACCOUNT), enabled: true, variant: 'secondary' });
  // } else if (user) {
  //   buttons.push({ text: 'Upgrade to Play Every AI', action: () => onNavigate(MENU_SCENES.PRICING), enabled: true, variant: 'primary' });
  // } else {
  //   buttons.push({ text: 'Sign in with Google', action: handleGoogleSignIn, enabled: true, variant: 'secondary' });
  // }

  const buttonCount = buttons.length;

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowUp' || e.key === 'w') {
        e.preventDefault();
        setUsingKeyboard(true);
        setAnimationKey(k => k + 1);
        setSelectedIndex((prev) => (prev - 1 + buttonCount) % buttonCount);
      } else if (e.key === 'ArrowDown' || e.key === 's') {
        e.preventDefault();
        setUsingKeyboard(true);
        setAnimationKey(k => k + 1);
        setSelectedIndex((prev) => (prev + 1) % buttonCount);
      } else if (e.key === 'Enter') {
        const btn = buttons[selectedIndex];
        if (btn && btn.enabled) btn.action();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedIndex, setSelectedIndex, buttonCount, onNavigate, buttons]);

  const handleMouseEnter = (idx) => {
    setUsingKeyboard(false);
    setSelectedIndex(idx);
  };

  return (
    <div className="menu-content main-menu-content">
      <div className="menu-title-container">
        <GOLTitleBackground
          cellSize={6}
          speed={960}
          baseOpacity={0.04}
          opacityVariation={0.12}
          brightnessBase={0.5}
          brightnessVariation={0.5}
          color={[97, 242, 255]}
          initialDelay={3500}
        />
        <h1 className="menu-title"><span className="menu-title-line">Philosophers</span><br /><span className="menu-title-line">Foo<span className="menu-title-t">t</span>ball</span></h1>
      </div>
      <div className="menu-epigraph">
        <span className="menu-epigraph__line">
          {"\u266A Turing\u2019s fond of the football field \u266A".split('').map((ch, i) => (
            <span key={i} className="menu-epigraph__char" style={{ animationDelay: `${0.7 + i * 0.035}s` }}>
              {ch === ' ' ? '\u00A0' : ch}
            </span>
          ))}
        </span>
        <span className="menu-epigraph__line">
          {"\u266A For geometric problems the touch-lines yield \u266A".split('').map((ch, i) => (
            <span key={i} className="menu-epigraph__char" style={{ animationDelay: `${1.7 + i * 0.03}s` }}>
              {ch === ' ' ? '\u00A0' : ch}
            </span>
          ))}
        </span>
        <span className="menu-epigraph__ripple" />
        <span className="menu-epigraph__ripple menu-epigraph__ripple--second" />
      </div>
      <div className="menu-buttons">
        {buttons.map((btn, idx) => {
          const buttonNode = (
            <MenuButton
              text={btn.text}
              onClick={btn.action}
              isSelected={idx === selectedIndex}
              disabled={!btn.enabled}
              variant={btn.variant || 'default'}
              onMouseEnter={() => handleMouseEnter(idx)}
              usingKeyboard={usingKeyboard}
              animationKey={animationKey}
            />
          );
          if (btn.onDiscard) {
            return (
              <div className="menu-button-with-discard" key={btn.text}>
                {buttonNode}
                <button
                  type="button"
                  className="menu-button__discard"
                  onClick={(e) => { e.stopPropagation(); btn.onDiscard(); e.currentTarget.blur(); }}
                  onMouseEnter={(e) => e.stopPropagation()}
                  aria-label="Dismiss rejoin offer"
                  title="Dismiss"
                >
                  ×
                </button>
              </div>
            );
          }
          return <React.Fragment key={btn.text}>{buttonNode}</React.Fragment>;
        })}
        {savedSession && (
          <button
            className="discard-session-link"
            onClick={onDiscardSession}
          >
            Discard saved game
          </button>
        )}
      </div>
    </div>
  );
};

/**
 * VS Computer Config Scene
 */
const VsComputerConfig = ({ onBack, onStartGame }) => {
  const [playerSide, setPlayerSide] = useState(1); // 1 = P1, 2 = P2
  const [provider, setProvider] = useState('Anthropic');
  const [model, setModel] = useState('claude-opus-4-8');
  const [rows, setRows] = useState(DEFAULT_BOARD_SIZE.rows);
  const [cols, setCols] = useState(DEFAULT_BOARD_SIZE.cols);
  const [nnVisualizationEnabled, setNnVisualizationEnabled] = useState(false);
  const [llmPlanMode, setLlmPlanMode] = useState('single');
  const [systemPromptOpen, setSystemPromptOpen] = useState(false);
  const [systemPrompt, setSystemPrompt] = useState(() => {
    try {
      const saved = localStorage.getItem('phutball_llm_system_prompt');
      if (saved) return saved;
    } catch (e) { /* ignore */ }
    return buildSystemPrompt();
  });

  // Auth state
  const { user, tier, credits } = useAuth();
  const isPaid = tier !== 'free' && user;

  // API key state
  const [apiKeys, setApiKeys] = useState(loadApiKeys);
  const [isEditingApiKey, setIsEditingApiKey] = useState(false);
  const apiKeyInputRef = useRef(null);

  // Keyboard navigation state
  const [focusRow, setFocusRow] = useState(5); // Start on action buttons
  const [focusCol, setFocusCol] = useState(1); // Start on "Start Game"
  const [usingKeyboard, setUsingKeyboard] = useState(false);
  const [isEditingRows, setIsEditingRows] = useState(false);
  const [isEditingCols, setIsEditingCols] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const isAlphaZeroSize = ALPHAZERO_SUPPORTED_SIZES.some(([r, c]) => r === rows && c === cols);
  const isSupported = provider !== 'AlphaZero' || isAlphaZeroSize;
  const providers = Object.keys(LLM_PROVIDERS);
  const models = LLM_PROVIDERS[provider] || [];
  const showLlmPlanMode = provider !== 'AlphaZero';
  // Check if this model has paid credits remaining
  const modelCredit = credits.find(c => c.model === model);
  const hasCredits = isPaid && modelCredit && modelCredit.used < modelCredit.total;

  // Only require API key for non-AlphaZero providers when user doesn't have credits
  const requiresApiKey = provider !== 'AlphaZero' && !hasCredits;
  const currentApiKey = apiKeys[provider] || '';
  const hasApiKey = currentApiKey.length > 0;
  const isEditing = isEditingRows || isEditingCols || isDropdownOpen || isEditingApiKey;

  useEffect(() => {
    // Reset model when provider changes
    setModel(LLM_PROVIDERS[provider]?.[0] || '');
  }, [provider]);

  const handleApiKeyChange = (e) => {
    const newKey = e.target.value;
    const updated = { ...apiKeys, [provider]: newKey };
    setApiKeys(updated);
    saveApiKeys(updated);
  };

  const handleStartGame = () => {
    // Only pass a custom system prompt if the user actually modified it.
    const defaultPrompt = buildSystemPrompt();
    const systemPromptOverride = systemPrompt && systemPrompt !== defaultPrompt ? systemPrompt : null;

    const config = {
      p1: playerSide === 1 ? 'Human' : provider,
      p2: playerSide === 2 ? 'Human' : provider,
      modelName: model,
      rows,
      cols,
      nnVisualization: nnVisualizationEnabled,
      gameMode: 'vs_computer',
      llmPlanMode,
      systemPromptOverride,
      // Paid users with credits don't need to provide API key, server uses platform key
      apiKey: (requiresApiKey && !hasCredits) ? currentApiKey : null,
    };
    onStartGame(config);
  };
  
  const updateRows = (newValue) => {
    let val = newValue;
    if (val % 2 === 0) val = val + 1;
    setRows(Math.max(9, Math.min(99, val)));
  };
  
  const updateCols = (newValue) => {
    let val = newValue;
    if (val % 2 === 0) val = val + 1;
    val = Math.max(1, Math.min(99, val));
    if (val === 3) {
      val = cols > 3 ? 1 : 5;
    }
    setCols(val);
  };
  
  // Grid structure - dynamic based on whether LLM plan mode is shown
  // Row 0: [Player 1, Player 2] - 2 items
  // Row 1: [Provider dropdown, Model dropdown] - 2 items
  // Row 2: [LLM Single, LLM Full] - 2 items (only if showLlmPlanMode)
  // Row 3: [Rows-, RowsBadge, Rows+, Cols-, ColsBadge, Cols+] - 6 items
  // Row 4: [NN Visualization] - 1 item
  // Row 5: [Back, Start] - 2 items

  const getGridStructure = () => {
    if (showLlmPlanMode) {
      return [2, 2, 2, 6, 1, 2]; // With LLM plan mode row
    }
    return [2, 2, 6, 1, 2]; // Without LLM plan mode row
  };
  
  const gridStructure = getGridStructure();
  
  // Map logical row to actual row (accounting for conditional LLM row)
  const getActualRow = (logicalRow) => {
    if (!showLlmPlanMode && logicalRow >= 2) {
      return logicalRow + 1; // Skip LLM row
    }
    return logicalRow;
  };
  
  const getLogicalRow = (actualRow) => {
    if (!showLlmPlanMode && actualRow >= 2) {
      return actualRow - 1;
    }
    return actualRow;
  };
  
  const handleKeyDown = useCallback((e) => {
    if (isEditing) return;
    // Bail if the keydown originates inside any text input — otherwise typing
    // letters like w/a/s/d in the system-prompt textarea triggers menu nav.
    const tag = e.target?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable) return;

    const key = e.key.toLowerCase();

    if (['arrowup', 'arrowdown', 'arrowleft', 'arrowright', 'w', 'a', 's', 'd', 'enter', ' ', 'escape'].includes(key)) {
      e.preventDefault();
      setUsingKeyboard(true);

      if (key === 'escape') {
        onBack();
        return;
      }

      if (key === 'arrowup' || key === 'w') {
        setFocusRow(prev => {
          const newRow = Math.max(0, prev - 1);
          setFocusCol(prevCol => Math.min(prevCol, gridStructure[newRow] - 1));
          return newRow;
        });
      } else if (key === 'arrowdown' || key === 's') {
        setFocusRow(prev => {
          const newRow = Math.min(gridStructure.length - 1, prev + 1);
          setFocusCol(prevCol => Math.min(prevCol, gridStructure[newRow] - 1));
          return newRow;
        });
      } else if (key === 'arrowleft' || key === 'a') {
        setFocusCol(prev => Math.max(0, prev - 1));
      } else if (key === 'arrowright' || key === 'd') {
        setFocusCol(prev => Math.min(gridStructure[focusRow] - 1, prev + 1));
      } else if (key === 'enter' || key === ' ') {
        const actualRow = getActualRow(focusRow);
        
        if (actualRow === 0) {
          // Player side
          if (focusCol === 0) setPlayerSide(1);
          else if (focusCol === 1) setPlayerSide(2);
        } else if (actualRow === 1) {
          // Dropdowns - handled by DropdownSelector component itself
          // Do nothing here, let the event propagate to the dropdown
        } else if (actualRow === 2 && showLlmPlanMode) {
          // LLM Plan Mode
          if (focusCol === 0) setLlmPlanMode('single');
          else if (focusCol === 1) setLlmPlanMode('full_sequence');
        } else if (actualRow === 3 || (!showLlmPlanMode && actualRow === 2)) {
          // Rows/Cols - actual row 3, or row 2 if no LLM mode
          if (focusCol === 0) updateRows(rows - 2);
          else if (focusCol === 1) setIsEditingRows(true);
          else if (focusCol === 2) updateRows(rows + 2);
          else if (focusCol === 3) updateCols(cols - 2);
          else if (focusCol === 4) setIsEditingCols(true);
          else if (focusCol === 5) updateCols(cols + 2);
        } else if (actualRow === 4 || (!showLlmPlanMode && actualRow === 3)) {
          // NN Visualization
          if (focusCol === 0) setNnVisualizationEnabled(!nnVisualizationEnabled);
        } else if (actualRow === 5 || (!showLlmPlanMode && actualRow === 4)) {
          // Action buttons
          if (focusCol === 0) onBack();
          else if (focusCol === 1) handleStartGame();
        }
      }
    }
  }, [focusRow, focusCol, gridStructure, onBack, handleStartGame, provider, providers, model, models, showLlmPlanMode, rows, cols, nnVisualizationEnabled, isEditing]);
  
  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);
  
  const handleMouseEnter = (row, col) => {
    if (isEditing) return;
    setUsingKeyboard(false);
    setFocusRow(row);
    setFocusCol(col);
  };
  
  const isSelected = (row, col) => usingKeyboard && focusRow === row && focusCol === col;
  
  // Calculate row indices based on whether LLM mode is shown
  const playerRow = 0;
  const dropdownRow = 1;
  const llmRow = showLlmPlanMode ? 2 : -1;
  const sizeRow = showLlmPlanMode ? 3 : 2;
  const toggleRow = showLlmPlanMode ? 4 : 3;
  const actionRow = showLlmPlanMode ? 5 : 4;
  
  return (
    <div className="menu-content config-content">
      <h2 className="config-title">Play vs Computer</h2>
      
      <div className="config-section">
        <div className="config-row">
          <span className="config-label">Play as:</span>
          <div className="toggle-group">
            <MenuButton
              text="Player 1"
              onClick={() => setPlayerSide(1)}
              onMouseEnter={() => handleMouseEnter(playerRow, 0)}
              isSelected={isSelected(playerRow, 0)}
              isActive={playerSide === 1}
              variant="toggle"
              usingKeyboard={usingKeyboard}
            />
            <MenuButton
              text="Player 2"
              onClick={() => setPlayerSide(2)}
              onMouseEnter={() => handleMouseEnter(playerRow, 1)}
              isSelected={isSelected(playerRow, 1)}
              isActive={playerSide === 2}
              variant="toggle"
              usingKeyboard={usingKeyboard}
            />
          </div>
        </div>
        
        <div className="config-row">
          <DropdownSelector
            label="AI Provider"
            value={provider}
            options={providers}
            disabledOptions={[]}
            onChange={setProvider}
            isSelected={isSelected(dropdownRow, 0)}
            onMouseEnter={() => handleMouseEnter(dropdownRow, 0)}
            usingKeyboard={usingKeyboard}
            onOpenChange={setIsDropdownOpen}
          />
          <DropdownSelector
            label="Model"
            value={model}
            options={models}
            onChange={setModel}
            isSelected={isSelected(dropdownRow, 1)}
            onMouseEnter={() => handleMouseEnter(dropdownRow, 1)}
            usingKeyboard={usingKeyboard}
            onOpenChange={setIsDropdownOpen}
          />
        </div>
        
        {showLlmPlanMode && (
          <div className="config-row">
            <span className="config-label">LLM Plan Mode:</span>
            <div className="toggle-group">
              <MenuButton
                text="Single Action"
                onClick={() => setLlmPlanMode('single')}
                onMouseEnter={() => handleMouseEnter(llmRow, 0)}
                isSelected={isSelected(llmRow, 0)}
                isActive={llmPlanMode === 'single'}
                variant="toggle"
                usingKeyboard={usingKeyboard}
              />
              <MenuButton
                text="Full Sequence"
                onClick={() => setLlmPlanMode('full_sequence')}
                onMouseEnter={() => handleMouseEnter(llmRow, 1)}
                isSelected={isSelected(llmRow, 1)}
                isActive={llmPlanMode === 'full_sequence'}
                variant="toggle"
                usingKeyboard={usingKeyboard}
              />
            </div>
          </div>
        )}

        {/* Credit display for paid users */}
        {isPaid && provider !== 'AlphaZero' && modelCredit && (
          <div className="config-row">
            <div className="credit-badge">
              {modelCredit.total - modelCredit.used}/{modelCredit.total} games remaining for {model}
            </div>
          </div>
        )}

        {/* API Key Input - only for LLM providers without paid credits */}
        {requiresApiKey && (
          <div className="config-row config-row--api-key">
            <div className="api-key-input-group">
              <label className="api-key-label">
                {provider} API Key
                {API_KEY_URLS[provider] && (
                  <a
                    href={API_KEY_URLS[provider]}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="api-key-link"
                    title={`Get ${provider} API key`}
                  >
                    (get key)
                  </a>
                )}
              </label>
              <input
                ref={apiKeyInputRef}
                type="password"
                className={`api-key-input ${hasApiKey ? 'api-key-input--filled' : ''}`}
                value={currentApiKey}
                onChange={handleApiKeyChange}
                onFocus={() => setIsEditingApiKey(true)}
                onBlur={() => setIsEditingApiKey(false)}
                placeholder={`Enter ${provider} API key...`}
                autoComplete="off"
              />
            </div>
            <div className="api-key-notice">
              Keys stored in session only (cleared when tab closes)
            </div>
          </div>
        )}
      </div>
      
      <div className="config-section">
        <div className="config-row config-row--centered">
          <div className="arrow-selector">
            <MenuButton
              text="‹"
              onClick={() => updateRows(rows - 2)}
              onMouseEnter={() => handleMouseEnter(sizeRow, 0)}
              disabled={rows <= 9}
              variant="arrow"
              isSelected={isSelected(sizeRow, 0)}
              usingKeyboard={usingKeyboard}
            />
            <EditableBadge
              label="Rows"
              value={rows}
              onChange={updateRows}
              min={9}
              max={99}
              ensureOdd={true}
              isSelected={isSelected(sizeRow, 1)}
              onMouseEnter={() => handleMouseEnter(sizeRow, 1)}
              usingKeyboard={usingKeyboard}
              forceEdit={isEditingRows}
              onEditingChange={setIsEditingRows}
            />
            <MenuButton
              text="›"
              onClick={() => updateRows(rows + 2)}
              onMouseEnter={() => handleMouseEnter(sizeRow, 2)}
              disabled={rows >= 99}
              variant="arrow"
              isSelected={isSelected(sizeRow, 2)}
              usingKeyboard={usingKeyboard}
            />
          </div>
          <div className="arrow-selector">
            <MenuButton
              text="‹"
              onClick={() => updateCols(cols - 2)}
              onMouseEnter={() => handleMouseEnter(sizeRow, 3)}
              disabled={cols <= 1}
              variant="arrow"
              isSelected={isSelected(sizeRow, 3)}
              usingKeyboard={usingKeyboard}
            />
            <EditableBadge
              label="Cols"
              value={cols}
              onChange={updateCols}
              min={1}
              max={99}
              ensureOdd={true}
              isSelected={isSelected(sizeRow, 4)}
              onMouseEnter={() => handleMouseEnter(sizeRow, 4)}
              usingKeyboard={usingKeyboard}
              forceEdit={isEditingCols}
              onEditingChange={setIsEditingCols}
            />
            <MenuButton
              text="›"
              onClick={() => updateCols(cols + 2)}
              onMouseEnter={() => handleMouseEnter(sizeRow, 5)}
              disabled={cols >= 99}
              variant="arrow"
              isSelected={isSelected(sizeRow, 5)}
              usingKeyboard={usingKeyboard}
            />
          </div>
        </div>
        
        {!isSupported && (
          <div className="config-warning">
            AlphaZero only supports: {ALPHAZERO_SUPPORTED_SIZES.map(([r, c]) => `${r}×${c}`).join(', ')}
          </div>
        )}
      </div>
      
      <div className="config-section">
        <div className="config-row">
          <MenuButton
            text={`NN Visualization: ${nnVisualizationEnabled ? 'ON' : 'OFF'}`}
            onClick={() => setNnVisualizationEnabled(!nnVisualizationEnabled)}
            onMouseEnter={() => handleMouseEnter(toggleRow, 0)}
            isSelected={isSelected(toggleRow, 0)}
            isActive={nnVisualizationEnabled}
            disabled={provider !== 'AlphaZero' || !isAlphaZeroSize}
            variant="toggle"
            usingKeyboard={usingKeyboard}
          />
        </div>
      </div>

      {provider !== 'AlphaZero' && (
        <div className="config-section">
          <button
            type="button"
            className="system-prompt-toggle"
            onClick={(e) => { setSystemPromptOpen((o) => !o); e.currentTarget.blur(); }}
          >
            {systemPromptOpen ? '▼' : '▶'} System prompt
            {systemPrompt !== buildSystemPrompt() && (
              <span className="system-prompt-modified"> (modified)</span>
            )}
          </button>
          {systemPromptOpen && (
            <div className="system-prompt-editor">
              <textarea
                className="system-prompt-textarea"
                value={systemPrompt}
                onChange={(e) => {
                  setSystemPrompt(e.target.value);
                  try { localStorage.setItem('phutball_llm_system_prompt', e.target.value); } catch (err) { /* quota */ }
                }}
                spellCheck={false}
                rows={16}
              />
              <div className="system-prompt-actions">
                <span className="system-prompt-count">
                  {(() => {
                    const approxTokens = estimateTokens(systemPrompt);
                    const price = lookupPrice(model);
                    const cachedPerTurn = price ? (approxTokens / 1_000_000) * price.input * 0.1 : null;
                    const writeOnce = price ? (approxTokens / 1_000_000) * price.input * 1.25 : null;
                    return (
                      <>
                        ~{approxTokens.toLocaleString()} tokens · {systemPrompt.length.toLocaleString()} chars
                        {price && (
                          <>
                            {' · '}first turn ≈ ${writeOnce.toFixed(4)}, cached turn ≈ ${cachedPerTurn.toFixed(4)}
                          </>
                        )}
                      </>
                    );
                  })()}
                </span>
                <button
                  type="button"
                  className="system-prompt-reset"
                  onClick={(e) => {
                    const fresh = buildSystemPrompt();
                    setSystemPrompt(fresh);
                    try { localStorage.removeItem('phutball_llm_system_prompt'); } catch (err) { /* ignore */ }
                    e.currentTarget.blur();
                  }}
                  disabled={systemPrompt === buildSystemPrompt()}
                >
                  Reset to default
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="config-actions">
        <MenuButton 
          text="Back" 
          onClick={onBack}
          onMouseEnter={() => handleMouseEnter(actionRow, 0)}
          variant="secondary"
          isSelected={isSelected(actionRow, 0)}
          usingKeyboard={usingKeyboard}
        />
        <MenuButton 
          text="Start Game" 
          onClick={handleStartGame}
          onMouseEnter={() => handleMouseEnter(actionRow, 1)}
          variant="primary"
          isSelected={isSelected(actionRow, 1)}
          usingKeyboard={usingKeyboard}
          disabled={!isSupported && provider !== 'Human'}
        />
      </div>
    </div>
  );
};

/**
 * Puzzle Mode Select Scene (intermediate picker).
 *
 * Choose between solving puzzles yourself (manual practice, eventually a
 * random-puzzle stream) or watching an LLM attempt them (BYOK). Routes
 * onward to PuzzleConfig with `puzzleAttemptMode` stamped so downstream
 * scenes can branch.
 */
const PuzzleModeSelect = ({ onBack, onPickMode }) => {
  return (
    <div className="config-screen">
      <h2 className="config-title">PUZZLES</h2>
      <p className="config-subtitle">
        Pick a mode.
      </p>

      <div className="config-section" style={{ display: 'flex', gap: '12px', flexDirection: 'column' }}>
        <MenuButton text="Solve puzzles" onClick={() => onPickMode('manual')} variant="primary" />
        <MenuButton text="Endless puzzles" onClick={() => onPickMode('endless')} variant="primary" />
        <MenuButton text="Watch an LLM" onClick={() => onPickMode('llm')} variant="secondary" />
      </div>

      <div className="config-section" style={{ marginTop: '24px' }}>
        <MenuButton text="Back" onClick={onBack} variant="default" />
      </div>
    </div>
  );
};

/**
 * Endless Puzzle Config Scene.
 *
 * Lets the user pick which puzzle types are in the rotation and the
 * difficulty range (min/max num_jumps) for each. The selected config
 * lives on the config payload so the runtime's queue hook can fetch
 * with the user's bounds. First three puzzles always serve fast types
 * regardless of toggles below, to avoid waiting at puzzle 1 while a
 * threat is being generated in the background.
 */
// Per-type n-dial label mirrors what the single-puzzle config shows so
// the endless config feels familiar (range bounds instead of an exact
// value, since endless mode randomises within the bounds).
const N_LABEL = {
  win:         'Jumps to win',
  threat:      'Threat depth (jumps)',
  passthrough: 'Jumps in sequence',
  denial:      'Total jumps',  // sampled in [4, cap] server-side; cap == maxN
};

// Cap presets for endless mode. 0 = no cap (run until first mistake).
const RUN_LENGTH_OPTIONS = [
  { id: 'endless',    label: 'Endless',     cap: 0  },
  { id: 'twelve',     label: '12 puzzles',  cap: 12 },
  { id: 'twentyfour', label: '24 puzzles',  cap: 24 },
];

const ENDLESS_PER_TYPE_DEFAULTS = {
  win:         { enabled: true, minN: 1, maxN: 4, noise: 10, maxJumpLen: 3 },
  threat:      { enabled: true, minN: 2, maxN: 4 },
  passthrough: { enabled: true, minN: 2, maxN: 4 },
  denial:      { enabled: true, minN: 4, maxN: 6 },
};

const EndlessPuzzleConfig = ({ onBack, onStartGame }) => {
  const [rows] = useState(DEFAULT_BOARD_SIZE.rows);
  const [cols] = useState(DEFAULT_BOARD_SIZE.cols);
  // URL-driven defaults so /puzzles/endless?types=win,threat&run=twelve
  // pre-fills toggles and the run length on cold paste.
  const [searchParams] = useSearchParams();
  const urlDefaults = parseEndlessSearchParams(searchParams, ENDLESS_PER_TYPE_DEFAULTS);

  // Per-type enable + n-range. Win additionally carries noise +
  // max_jump_len, matching the single-puzzle config knobs.
  const [perType, setPerType] = useState(() => urlDefaults.perType);
  const [runLengthId, setRunLengthId] = useState(urlDefaults.runLengthId || 'endless');
  const runLength = RUN_LENGTH_OPTIONS.find(o => o.id === runLengthId) || RUN_LENGTH_OPTIONS[0];

  const updateType = (id, patch) => {
    setPerType(prev => ({ ...prev, [id]: { ...prev[id], ...patch } }));
  };

  const enabledTypes = PUZZLE_TYPES_BASE.filter(t => perType[t.id]?.enabled).map(t => t.id);
  const canStart = enabledTypes.length > 0;

  const handleStart = () => {
    const config = {
      p1: 'Human',
      p2: 'Human',
      modelName: null,
      rows,
      cols,
      gameMode: 'puzzle',
      puzzleAttemptMode: 'endless',
      endless: {
        enabledTypes,
        runLengthId,
        cap: runLength.cap,  // 0 = no cap, else exact puzzle count
        perTypeConfig: Object.fromEntries(
          PUZZLE_TYPES_BASE.map(t => {
            const c = perType[t.id];
            return [t.id, {
              minN: c.minN,
              maxN: c.maxN,
              noise: t.id === 'win' ? (c.noise ?? 10) : 0,
              maxJumpLen: t.id === 'win' ? (c.maxJumpLen ?? 3) : 3,
            }];
          }),
        ),
      },
    };
    onStartGame(config);
  };

  return (
    <div className="config-screen">
      <h2 className="config-title">ENDLESS PUZZLES</h2>
      <p className="config-subtitle">
        Endless: run until your first mistake.
        Capped runs: finish without a mistake within the cap to set a fastest-time highscore.
        Toggle types and tune difficulty per type below; threat puzzles are capped to ~1 in 4 because they take longer to generate.
      </p>

      <div className="config-section">
        <label className="config-label">Run length</label>
        <div className="config-row" style={{ gap: '8px', flexWrap: 'wrap' }}>
          {RUN_LENGTH_OPTIONS.map(opt => (
            <MenuButton
              key={opt.id}
              text={opt.label}
              onClick={() => setRunLengthId(opt.id)}
              variant={runLengthId === opt.id ? 'primary' : 'default'}
              isSelected={runLengthId === opt.id}
            />
          ))}
        </div>
      </div>

      {PUZZLE_TYPES_BASE.map(t => {
        const cfg = perType[t.id];
        const label = N_LABEL[t.id] || 'n';
        return (
          <div key={t.id} className="config-section">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <MenuButton
                text={cfg.enabled ? `${t.label} : ON` : `${t.label} : off`}
                onClick={() => updateType(t.id, { enabled: !cfg.enabled })}
                variant={cfg.enabled ? 'primary' : 'default'}
                isSelected={cfg.enabled}
              />
              <span style={{ opacity: 0.55, fontSize: '0.7em' }}>{t.hint}</span>
            </div>
            {cfg.enabled && (
              <>
                <div className="config-row" style={{ gap: '12px', flexWrap: 'wrap' }}>
                  <ArrowSelector
                    label={`Min ${label}`}
                    value={cfg.minN}
                    onPrev={() => updateType(t.id, { minN: Math.max(t.min, cfg.minN - 1) })}
                    onNext={() => updateType(t.id, { minN: Math.min(cfg.maxN, cfg.minN + 1) })}
                    min={t.min}
                    max={cfg.maxN}
                    showRange
                  />
                  <ArrowSelector
                    label={`Max ${label}`}
                    value={cfg.maxN}
                    onPrev={() => updateType(t.id, { maxN: Math.max(cfg.minN, cfg.maxN - 1) })}
                    onNext={() => updateType(t.id, { maxN: Math.min(t.max, cfg.maxN + 1) })}
                    min={cfg.minN}
                    max={t.max}
                    showRange
                  />
                </div>
                {t.id === 'win' && (
                  <div className="config-row" style={{ gap: '12px', flexWrap: 'wrap' }}>
                    <ArrowSelector
                      label="Max noise stones"
                      value={cfg.noise ?? 10}
                      onPrev={() => updateType(t.id, { noise: Math.max(0, (cfg.noise ?? 10) - 1) })}
                      onNext={() => updateType(t.id, { noise: Math.min(20, (cfg.noise ?? 10) + 1) })}
                      min={0}
                      max={20}
                      showRange
                    />
                    <ArrowSelector
                      label="Max stones per jump"
                      value={cfg.maxJumpLen ?? 3}
                      onPrev={() => updateType(t.id, { maxJumpLen: Math.max(1, (cfg.maxJumpLen ?? 3) - 1) })}
                      onNext={() => updateType(t.id, { maxJumpLen: Math.min(8, (cfg.maxJumpLen ?? 3) + 1) })}
                      min={1}
                      max={8}
                      showRange
                    />
                  </div>
                )}
              </>
            )}
          </div>
        );
      })}

      <div className="config-actions">
        <MenuButton text="Back" onClick={onBack} variant="secondary" />
        <MenuButton
          text="Start endless run"
          onClick={handleStart}
          variant="primary"
          disabled={!canStart}
        />
      </div>
    </div>
  );
};

/**
 * Puzzle Config Scene (dev/eval).
 *
 * Seeds a curriculum puzzle (procedurally-generated state with a known
 * winning sequence) into a normal local-multiplayer game. Receives
 * `attemptMode` ('manual' | 'llm') from the picker; manual play is the
 * default and LLM-watch wiring lights up when attemptMode === 'llm'.
 */
const PUZZLE_TYPES_BASE = [
  { id: 'win',         label: 'Win',         min: 1, max: 8, hint: 'Find your scoring sequence' },
  { id: 'threat',      label: 'Threat',      min: 2, max: 6, hint: 'Disrupt opponent threat at a pivot' },
  { id: 'passthrough', label: 'Passthrough', min: 2, max: 8, hint: 'Advance via own-endzone passthrough' },
  { id: 'denial',      label: 'Denial',      min: 4, max: 9, hint: 'Consume opponent material before advancing' },
];

const PUZZLE_TYPES = [
  { id: 'win',         label: 'Win',         min: 1, max: 8, hint: 'Find your scoring sequence' },
  // Threat capped at 6 — opponent-recovery search makes deeper chains slow
  // (~11s at n=6, ~20s+ beyond). Bump if the validator is sped up.
  { id: 'threat',      label: 'Threat',      min: 2, max: 6, hint: 'Disrupt opponent’s win at a pivot' },
  { id: 'passthrough', label: 'Passthrough', min: 2, max: 8, hint: 'Advance via own-endzone passthrough' },
  // Denial puzzles need a real loop+advance structure: the loop component
  // alone is at least 3-4 jumps to consume meaningful material before
  // returning to start, and the advance component adds more on top.
  { id: 'denial',      label: 'Denial',      min: 4, max: 9, hint: 'Consume opponent material before advancing' },
];

// Providers offered for "Watch an LLM". The four flagships plus OpenRouter
// for the wider open-weights pool (Kimi, DeepSeek, Qwen, Llama, Mistral...).
// AlphaZero is intentionally excluded; it is not an LLM and routes through
// the on-device worker instead of the BYOK call path.
const LLM_PUZZLE_PROVIDERS = ['Anthropic', 'OpenAI', 'Google', 'XAI', 'OpenRouter'];

const PuzzleConfig = ({ onBack, onStartGame, attemptMode = 'manual' }) => {
  // URL-driven defaults: a visitor landing on /puzzles/solve?type=denial&jumps=6
  // sees those knobs already chosen. Missing params fall through to the same
  // hardcoded defaults the screen had before.
  const [searchParams] = useSearchParams();
  const urlDefaults = parseSinglePuzzleSearchParams(searchParams);

  const [puzzleType, setPuzzleType] = useState(urlDefaults.puzzle_type || 'win');
  const [numJumps, setNumJumps] = useState(urlDefaults.num_jumps ?? 1);
  const [puzzlePlayer, setPuzzlePlayer] = useState(urlDefaults.player ?? 1);
  const [noise, setNoise] = useState(urlDefaults.noise ?? 10);
  const [maxJumpLen, setMaxJumpLen] = useState(urlDefaults.max_jump_len ?? 3);
  const [seed, setSeed] = useState(urlDefaults.seed != null ? String(urlDefaults.seed) : '');
  const [rows] = useState(DEFAULT_BOARD_SIZE.rows);
  const [cols] = useState(DEFAULT_BOARD_SIZE.cols);

  // LLM-mode state. Read previously-stored BYOK keys from sessionStorage so a
  // user who already configured a key under VS Computer doesn't have to retype.
  // For Watch mode, provider/model also seed from the URL when present.
  const initialProvider = (attemptMode === 'llm' && urlDefaults.llmProvider) || 'Anthropic';
  const initialModel = (attemptMode === 'llm' && urlDefaults.llmModel)
    || LLM_PROVIDERS[initialProvider]?.[0]
    || 'claude-opus-4-8';
  const [llmProvider, setLlmProvider] = useState(initialProvider);
  const [llmModel, setLlmModel] = useState(initialModel);
  const [llmApiKeys, setLlmApiKeys] = useState(loadApiKeys);
  const llmApiKey = llmApiKeys[llmProvider] || '';

  // Threat puzzles need n>=2 (no pivot exists for n=1). Auto-bump.
  useEffect(() => {
    const meta = PUZZLE_TYPES.find(t => t.id === puzzleType);
    if (meta && numJumps < meta.min) setNumJumps(meta.min);
  }, [puzzleType, numJumps]);

  // Reset model when provider changes.
  useEffect(() => {
    const models = LLM_PROVIDERS[llmProvider] || [];
    if (models.length && !models.includes(llmModel)) {
      setLlmModel(models[0]);
    }
  }, [llmProvider]); // eslint-disable-line react-hooks/exhaustive-deps

  // Noise only applies to win puzzles per methodology.
  const noiseEnabled = puzzleType === 'win';
  const typeMeta = PUZZLE_TYPES.find(t => t.id === puzzleType) || PUZZLE_TYPES[0];

  const handleStart = () => {
    const seedVal = seed === '' ? null : Math.max(0, parseInt(seed, 10) || 0);
    // For threat puzzles the dropdown selects the RESPONDER (the player
    // facing the threat); the server's `player` param refers to the
    // offensive player (the one whose winning line we're constructing),
    // which is the opposite. Invert here so the rest of the stack stays
    // simple.
    const serverPlayer = puzzleType === 'threat' ? (3 - puzzlePlayer) : puzzlePlayer;
    // In LLM mode, the player who actually moves on the puzzle (puzzlePlayer)
    // is driven by the LLM. Stamp that slot with the provider name so the
    // LogPanel turn header reads "Anthropic (claude-opus-4-7)" instead of
    // "Human". App.js's puzzleAIOverride still injects the apiKey separately.
    const isLLMAttempt = attemptMode === 'llm';
    const llmSide = puzzlePlayer; // 1 or 2; whoever makes the puzzle move
    const config = {
      p1: isLLMAttempt && llmSide === 1 ? llmProvider : 'Human',
      p2: isLLMAttempt && llmSide === 2 ? llmProvider : 'Human',
      modelName: isLLMAttempt ? llmModel : null,
      rows,
      cols,
      gameMode: 'puzzle',
      puzzleAttemptMode: attemptMode,
      // In LLM mode the puzzle player slot is driven by the LLM via
      // App.js's puzzleAIOverride hook; provider/model/apiKey ride along
      // here so App can wire `requestLLMMove` without re-prompting.
      ...(isLLMAttempt ? {
        llmProvider,
        llmModel,
        llmApiKey: llmApiKey || null,
      } : {}),
      puzzle: {
        puzzle_type: puzzleType,
        num_jumps: Math.max(typeMeta.min, Math.min(typeMeta.max ?? 4, numJumps)),
        player: serverPlayer,
        noise: noiseEnabled ? noise : 0,
        max_jump_len: maxJumpLen,
        ...(seedVal !== null ? { seed: seedVal } : {}),
      },
    };
    onStartGame(config);
  };

  const handleApiKeyChange = (e) => {
    const newKey = e.target.value;
    const updated = { ...llmApiKeys, [llmProvider]: newKey };
    setLlmApiKeys(updated);
    saveApiKeys(updated);
  };

  const llmReady = !!(llmProvider && llmModel && llmApiKey);

  const isLLM = attemptMode === 'llm';

  return (
    <div className="config-screen">
      <h2 className="config-title">{isLLM ? 'WATCH AN LLM' : 'SOLVE PUZZLES'}</h2>
      <p className="config-subtitle">
        {typeMeta.hint}.{' '}
        {isLLM
          ? 'Pick a model and provide its API key below. The LLM sees only the board state, while the canonical line stays visible to you so you can compare its reasoning to the intended solution.'
          : 'The solution stays hidden until you commit your move; the canonical line is shown afterward for comparison.'}
      </p>

      <div className="config-section">
        <label className="config-label">Puzzle type</label>
        <div className="config-row" style={{ gap: '6px', flexWrap: 'wrap' }}>
          {PUZZLE_TYPES.map(t => (
            <MenuButton
              key={t.id}
              text={t.label}
              onClick={() => setPuzzleType(t.id)}
              variant={puzzleType === t.id ? 'primary' : 'default'}
              isSelected={puzzleType === t.id}
            />
          ))}
        </div>
      </div>

      <div className="config-section">
        <ArrowSelector
          label={
            puzzleType === 'threat'      ? 'Threat depth (jumps)'
            : puzzleType === 'denial'    ? 'Max total jumps'
            : puzzleType === 'passthrough' ? 'Jumps in sequence'
            : 'Jumps to win'
          }
          value={Math.max(typeMeta.min, Math.min(typeMeta.max ?? 4, numJumps))}
          onPrev={() => setNumJumps(n => Math.max(typeMeta.min, n - 1))}
          onNext={() => setNumJumps(n => Math.min(typeMeta.max ?? 4, n + 1))}
          min={typeMeta.min}
          max={typeMeta.max ?? 4}
          showRange
        />
      </div>

      {noiseEnabled && (
        <div className="config-section">
          <ArrowSelector
            label="Max noise stones"
            value={noise}
            onPrev={() => setNoise(n => Math.max(0, n - 1))}
            onNext={() => setNoise(n => Math.min(20, n + 1))}
            min={0}
            max={20}
            showRange
          />
        </div>
      )}

      <div className="config-section">
        <ArrowSelector
          label="Max stones per jump"
          value={maxJumpLen}
          onPrev={() => setMaxJumpLen(n => Math.max(1, n - 1))}
          onNext={() => setMaxJumpLen(n => Math.min(8, n + 1))}
          min={1}
          max={8}
          showRange
        />
      </div>

      <div className="config-section">
        <label className="config-label">
          {puzzleType === 'threat' ? 'Player facing threat' : 'Player to move'}
        </label>
        <div className="config-row" style={{ gap: '8px' }}>
          <MenuButton
            text="P1 (top)"
            onClick={() => setPuzzlePlayer(1)}
            variant={puzzlePlayer === 1 ? 'primary' : 'default'}
            isSelected={puzzlePlayer === 1}
          />
          <MenuButton
            text="P2 (bottom)"
            onClick={() => setPuzzlePlayer(2)}
            variant={puzzlePlayer === 2 ? 'primary' : 'default'}
            isSelected={puzzlePlayer === 2}
          />
        </div>
      </div>

      <div className="config-section">
        <label className="config-label">Seed (blank = random)</label>
        <input
          type="text"
          value={seed}
          onChange={(e) => setSeed(e.target.value.replace(/\D/g, ''))}
          placeholder="e.g. 42"
          className="config-input"
          style={{ maxWidth: '160px', padding: '6px 10px' }}
        />
      </div>

      {isLLM && (
        <>
          <div className="config-section">
            <label className="config-label">LLM provider</label>
            <div className="config-row" style={{ gap: '6px', flexWrap: 'wrap' }}>
              {LLM_PUZZLE_PROVIDERS.map(p => (
                <MenuButton
                  key={p}
                  text={p}
                  onClick={() => setLlmProvider(p)}
                  variant={llmProvider === p ? 'primary' : 'default'}
                  isSelected={llmProvider === p}
                />
              ))}
            </div>
          </div>

          <div className="config-section">
            <label className="config-label">Model</label>
            <select
              value={llmModel}
              onChange={(e) => setLlmModel(e.target.value)}
              className="config-input"
              style={{ maxWidth: '320px', padding: '6px 10px' }}
            >
              {(LLM_PROVIDERS[llmProvider] || []).map(m => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>

          <div className="config-section">
            <label className="config-label">
              {llmProvider} API key
              {API_KEY_URLS[llmProvider] && (
                <a
                  href={API_KEY_URLS[llmProvider]}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="api-key-link"
                  title={`Get ${llmProvider} API key`}
                  style={{ marginLeft: '6px' }}
                >
                  (get key)
                </a>
              )}
              {llmApiKey ? (
                <span style={{ opacity: 0.6, fontSize: '0.7em', marginLeft: '6px' }}>· saved this session</span>
              ) : null}
            </label>
            <input
              type="password"
              value={llmApiKey}
              onChange={handleApiKeyChange}
              placeholder={`paste your ${llmProvider} key`}
              className="config-input"
              style={{ maxWidth: '420px', padding: '6px 10px' }}
              autoComplete="off"
            />
            <p style={{ opacity: 0.55, fontSize: '0.7em', marginTop: '4px' }}>
              Stored only in this tab's sessionStorage; cleared when the tab closes.
            </p>
          </div>
        </>
      )}

      <div className="config-actions">
        <MenuButton text="Back" onClick={onBack} variant="secondary" />
        <MenuButton
          text={isLLM ? 'Watch LLM' : 'Start Puzzle'}
          onClick={handleStart}
          variant="primary"
          disabled={isLLM && !llmReady}
        />
      </div>
    </div>
  );
};

/**
 * Local Multiplayer Config Scene
 */
const LocalMultiplayerConfig = ({ onBack, onStartGame }) => {
  const [rows, setRows] = useState(DEFAULT_BOARD_SIZE.rows);
  const [cols, setCols] = useState(DEFAULT_BOARD_SIZE.cols);
  const [nnVisualizationEnabled, setNnVisualizationEnabled] = useState(false);
  const [isEditingRows, setIsEditingRows] = useState(false);
  const [isEditingCols, setIsEditingCols] = useState(false);

  // Navigation state: [row, col]
  // Row 0: [Rows-, RowsBadge, Rows+, Cols-, ColsBadge, Cols+]
  // Row 1: [NN Visualization]
  // Row 2: [Back, Start]
  const [focusRow, setFocusRow] = useState(2);
  const [focusCol, setFocusCol] = useState(1); // Start on "Start Game"
  const [usingKeyboard, setUsingKeyboard] = useState(false);

  const isSupported = ALPHAZERO_SUPPORTED_SIZES.some(([r, c]) => r === rows && c === cols);

  // Check if currently editing any field
  const isEditing = isEditingRows || isEditingCols;

  const handleStartGame = () => {
    const config = {
      p1: 'Human',
      p2: 'Human',
      modelName: null,
      rows,
      cols,
      nnVisualization: nnVisualizationEnabled,
      gameMode: 'local_multiplayer',
    };
    onStartGame(config);
  };
  
  const updateRows = (newValue) => {
    // Ensure odd, min 9, max 99
    let val = newValue;
    if (val % 2 === 0) val = val + 1;
    setRows(Math.max(9, Math.min(99, val)));
  };
  
  const updateCols = (newValue) => {
    // Ensure odd, min 1, max 99
    // Special case: skip 3 (jump from 1 to 5)
    let val = newValue;
    if (val % 2 === 0) val = val + 1;
    val = Math.max(1, Math.min(99, val));
    // Skip 3 - it offers no real width
    if (val === 3) {
      val = cols > 3 ? 1 : 5; // If decreasing, go to 1; if increasing, go to 5
    }
    setCols(val);
  };
  
  // Grid structure for navigation
  const gridStructure = [
    6, // Row 0: 6 items (Rows-, RowsBadge, Rows+, Cols-, ColsBadge, Cols+)
    1, // Row 1: 1 item (NN Visualization)
    2, // Row 2: 2 items (Back, Start)
  ];
  
  const handleKeyDown = useCallback((e) => {
    // Don't handle navigation keys while editing
    if (isEditing) return;
    const tag = e.target?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable) return;

    const key = e.key.toLowerCase();

    if (['arrowup', 'arrowdown', 'arrowleft', 'arrowright', 'w', 'a', 's', 'd', 'enter', ' ', 'escape'].includes(key)) {
      e.preventDefault();
      setUsingKeyboard(true);

      if (key === 'escape') {
        onBack();
        return;
      }

      if (key === 'arrowup' || key === 'w') {
        setFocusRow(prev => {
          const newRow = Math.max(0, prev - 1);
          // Adjust column if new row has fewer items
          setFocusCol(prevCol => Math.min(prevCol, gridStructure[newRow] - 1));
          return newRow;
        });
      } else if (key === 'arrowdown' || key === 's') {
        setFocusRow(prev => {
          const newRow = Math.min(gridStructure.length - 1, prev + 1);
          // Adjust column if new row has fewer items  
          setFocusCol(prevCol => Math.min(prevCol, gridStructure[newRow] - 1));
          return newRow;
        });
      } else if (key === 'arrowleft' || key === 'a') {
        setFocusCol(prev => Math.max(0, prev - 1));
      } else if (key === 'arrowright' || key === 'd') {
        setFocusCol(prev => Math.min(gridStructure[focusRow] - 1, prev + 1));
      } else if (key === 'enter' || key === ' ') {
        // Activate the focused element
        if (focusRow === 0) {
          // Row/Col selectors
          if (focusCol === 0) updateRows(rows - 2);
          else if (focusCol === 1) setIsEditingRows(true);
          else if (focusCol === 2) updateRows(rows + 2);
          else if (focusCol === 3) updateCols(cols - 2);
          else if (focusCol === 4) setIsEditingCols(true);
          else if (focusCol === 5) updateCols(cols + 2);
        } else if (focusRow === 1) {
          // NN Visualization toggle
          if (focusCol === 0) setNnVisualizationEnabled(!nnVisualizationEnabled);
        } else if (focusRow === 2) {
          // Action buttons
          if (focusCol === 0) onBack();
          else if (focusCol === 1) handleStartGame();
        }
      }
    }
  }, [focusRow, focusCol, gridStructure, onBack, handleStartGame, nnVisualizationEnabled, rows, cols, isEditing]);
  
  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);
  
  const handleMouseEnter = (row, col) => {
    if (isEditing) return;
    setUsingKeyboard(false);
    setFocusRow(row);
    setFocusCol(col);
  };
  
  const isSelected = (row, col) => usingKeyboard && focusRow === row && focusCol === col;
  
  return (
    <div className="menu-content config-content">
      <h2 className="config-title">Local Multiplayer Setup</h2>
      
      <div className="config-section">
        <div className="config-row config-row--centered">
          <div className="arrow-selector">
            <MenuButton
              text="‹"
              onClick={() => updateRows(rows - 2)}
              onMouseEnter={() => handleMouseEnter(0, 0)}
              disabled={rows <= 9}
              variant="arrow"
              isSelected={isSelected(0, 0)}
              usingKeyboard={usingKeyboard}
            />
            <EditableBadge
              label="Rows"
              value={rows}
              onChange={updateRows}
              min={9}
              max={99}
              ensureOdd={true}
              isSelected={isSelected(0, 1)}
              onMouseEnter={() => handleMouseEnter(0, 1)}
              usingKeyboard={usingKeyboard}
              forceEdit={isEditingRows}
              onEditingChange={setIsEditingRows}
            />
            <MenuButton
              text="›"
              onClick={() => updateRows(rows + 2)}
              onMouseEnter={() => handleMouseEnter(0, 2)}
              disabled={rows >= 99}
              variant="arrow"
              isSelected={isSelected(0, 2)}
              usingKeyboard={usingKeyboard}
            />
          </div>
          <div className="arrow-selector">
            <MenuButton
              text="‹"
              onClick={() => updateCols(cols - 2)}
              onMouseEnter={() => handleMouseEnter(0, 3)}
              disabled={cols <= 1}
              variant="arrow"
              isSelected={isSelected(0, 3)}
              usingKeyboard={usingKeyboard}
            />
            <EditableBadge
              label="Cols"
              value={cols}
              onChange={updateCols}
              min={1}
              max={99}
              ensureOdd={true}
              isSelected={isSelected(0, 4)}
              onMouseEnter={() => handleMouseEnter(0, 4)}
              usingKeyboard={usingKeyboard}
              forceEdit={isEditingCols}
              onEditingChange={setIsEditingCols}
            />
            <MenuButton
              text="›"
              onClick={() => updateCols(cols + 2)}
              onMouseEnter={() => handleMouseEnter(0, 5)}
              disabled={cols >= 99}
              variant="arrow"
              isSelected={isSelected(0, 5)}
              usingKeyboard={usingKeyboard}
            />
          </div>
        </div>
      </div>
      
      <div className="config-section">
        <div className="config-row">
          <MenuButton
            text={`NN Visualization: ${nnVisualizationEnabled ? 'ON' : 'OFF'}`}
            onClick={() => setNnVisualizationEnabled(!nnVisualizationEnabled)}
            onMouseEnter={() => handleMouseEnter(1, 0)}
            isSelected={isSelected(1, 0)}
            isActive={nnVisualizationEnabled}
            disabled={!isSupported}
            variant="toggle"
            usingKeyboard={usingKeyboard}
          />
        </div>
      </div>
      
      <div className="config-actions">
        <MenuButton 
          text="Back" 
          onClick={onBack} 
          onMouseEnter={() => handleMouseEnter(2, 0)}
          variant="secondary" 
          isSelected={isSelected(2, 0)}
          usingKeyboard={usingKeyboard}
        />
        <MenuButton 
          text="Start Game" 
          onClick={handleStartGame} 
          onMouseEnter={() => handleMouseEnter(2, 1)}
          variant="primary" 
          isSelected={isSelected(2, 1)}
          usingKeyboard={usingKeyboard}
        />
      </div>
    </div>
  );
};

/**
 * Pricing Scene — shows subscription tiers
 */
const PricingScene = ({ onBack }) => {
  const { user, token, login } = useAuth();
  const [loading, setLoading] = useState(false);

  const handleCheckout = async (tier) => {
    if (!user) {
      // Trigger Google sign-in first
      if (window.google?.accounts?.id) {
        window.google.accounts.id.prompt();
      }
      return;
    }
    setLoading(true);
    try {
      const data = await apiFetch('/billing/checkout', {
        token,
        body: { tier },
      });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (e) {
      console.error('Checkout failed:', e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="menu-content config-content">
      <h2 className="config-title">Play Every AI</h2>
      <p className="pricing-subtitle">No API keys needed. Server handles all LLM calls.</p>

      <div className="pricing-grid">
        <div className="pricing-card">
          <h3>Monthly</h3>
          <div className="pricing-price">$10/mo</div>
          <ul className="pricing-features">
            <li>1 game per model per month</li>
            <li>~19 AI models included</li>
            <li>Cancel anytime</li>
          </ul>
          <MenuButton
            text={loading ? 'Loading...' : 'Subscribe'}
            onClick={() => handleCheckout('monthly')}
            variant="primary"
            disabled={loading}
          />
        </div>

        <div className="pricing-card pricing-card--featured">
          <h3>World Cup Season Pass</h3>
          <div className="pricing-price">$26</div>
          <div className="pricing-period">June 11 - July 19</div>
          <ul className="pricing-features">
            <li>1 game per model (~$1/day)</li>
            <li>Best value for the tournament</li>
            <li>Buy add-on packs for more games</li>
          </ul>
          <MenuButton
            text={loading ? 'Loading...' : 'Buy Season Pass'}
            onClick={() => handleCheckout('season_pass')}
            variant="primary"
            disabled={loading}
          />
        </div>

        <div className="pricing-card">
          <h3>Add-on Pack</h3>
          <div className="pricing-price">$10</div>
          <ul className="pricing-features">
            <li>+1 game with every model</li>
            <li>Stackable — buy multiple</li>
            <li>Requires active subscription</li>
          </ul>
          <MenuButton
            text={loading ? 'Loading...' : 'Buy Pack'}
            onClick={() => handleCheckout('addon')}
            variant="default"
            disabled={loading || !user}
          />
        </div>
      </div>

      <p className="pricing-note">BYOK (bring your own key) is always free.</p>

      <div className="config-actions">
        <MenuButton text="Back" onClick={onBack} variant="secondary" />
      </div>
    </div>
  );
};

/**
 * Leaderboard Scene — ELO rankings with scope tabs
 */
const LeaderboardScene = ({ onBack }) => {
  const { user } = useAuth();
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState('world'); // 'world' | 'country' | 'region'

  // Country code to flag emoji
  const flag = (code) => {
    if (!code || code.length !== 2) return '';
    return String.fromCodePoint(
      ...[...code.toUpperCase()].map(c => 0x1F1E6 + c.charCodeAt(0) - 65)
    );
  };

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ scope });
    if (scope === 'country' && user?.country_code) {
      params.set('country', user.country_code);
    } else if (scope === 'region' && user?.country_code && user?.region_code) {
      params.set('country', user.country_code);
      params.set('region', user.region_code);
    }
    apiFetch(`/leaderboard?${params}`)
      .then(data => setLeaderboard(data.leaderboard || []))
      .catch(e => console.error('Leaderboard fetch failed:', e))
      .finally(() => setLoading(false));
  }, [scope, user?.country_code, user?.region_code]);

  const canShowRegion = user?.country_code && user?.region_code;
  const canShowCountry = user?.country_code;

  return (
    <div className="menu-content config-content">
      <h2 className="config-title">Leaderboard</h2>

      <div className="lobby-tabs">
        <button
          className={`lobby-tab ${scope === 'world' ? 'lobby-tab--active' : ''}`}
          onClick={() => setScope('world')}
        >World</button>
        <button
          className={`lobby-tab ${scope === 'country' ? 'lobby-tab--active' : ''}`}
          onClick={() => setScope('country')}
          disabled={!canShowCountry}
          title={!canShowCountry ? 'Sign in to see country rankings' : ''}
        >{canShowCountry ? `${flag(user.country_code)} Country` : 'Country'}</button>
        <button
          className={`lobby-tab ${scope === 'region' ? 'lobby-tab--active' : ''}`}
          onClick={() => setScope('region')}
          disabled={!canShowRegion}
          title={!canShowRegion ? 'Sign in to see regional rankings' : ''}
        >Region</button>
      </div>

      {loading ? (
        <p style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>Loading...</p>
      ) : leaderboard.length === 0 ? (
        <p className="lobby-empty">No ranked players yet. Play a game!</p>
      ) : (
        <table className="leaderboard-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Player</th>
              <th>ELO</th>
              <th>Games</th>
            </tr>
          </thead>
          <tbody>
            {leaderboard.map((row) => (
              <tr key={row.user_id} className={row.user_id === user?.id ? 'leaderboard-row--you' : ''}>
                <td>{row.rank}</td>
                <td className="leaderboard-player">
                  {flag(row.country_code)}{' '}
                  {row.display_name}
                </td>
                <td className="leaderboard-elo">{row.elo_rating}</td>
                <td>{row.elo_games_played}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="config-actions">
        <MenuButton text="Back" onClick={onBack} variant="secondary" />
      </div>
    </div>
  );
};

/**
 * Floating auth widget — top-right of main menu
 */
const MenuAuthWidget = () => {
  const { user, logout, updateSettings, renderGoogleButton } = useAuth();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const ref = useRef(null);
  const btnRef = useRef(null);

  useEffect(() => {
    if (!dropdownOpen) return;
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [dropdownOpen]);

  // Render Google Sign-In button (visually hidden — custom overlay handles the look)
  useEffect(() => {
    if (user || !btnRef.current) return;
    renderGoogleButton(btnRef.current, { width: 140 });
  }, [user, renderGoogleButton]);

  if (!user) {
    return (
      <div className="menu-auth-btn menu-auth-btn--signin" key="gsi">
        <div className="menu-auth-btn__gsi" ref={btnRef} />
        <div className="menu-auth-btn__overlay" aria-hidden="true">
          <svg width="14" height="14" viewBox="0 0 18 18" fill="none">
            <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4"/>
            <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
            <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
            <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
          </svg>
          <span>Sign in</span>
        </div>
      </div>
    );
  }

  const displayName = user.display_name || user.email?.split('@')[0] || '?';

  return (
    <div className="menu-auth-btn" key="user" ref={ref}>
      <button className="menu-auth-user" onClick={() => setDropdownOpen(p => !p)}>
        {user.avatar_url ? (
          <img src={user.avatar_url} alt="" className="menu-auth-user__img" referrerPolicy="no-referrer" />
        ) : (
          <span className="menu-auth-user__initial">
            {displayName[0].toUpperCase()}
          </span>
        )}
        <span className="menu-auth-user__name">{displayName}</span>
      </button>
      {dropdownOpen && (
        <div className="menu-auth-dropdown">
          {user.email && <div className="menu-auth-dropdown__email">{user.email}</div>}
          <label className="menu-auth-dropdown__item menu-auth-dropdown__toggle">
            <input
              type="checkbox"
              checked={!!user.leaderboard_visible}
              onChange={() => updateSettings({ leaderboard_visible: !user.leaderboard_visible })}
            />
            Show on Leaderboard
          </label>
          <button className="menu-auth-dropdown__item" onClick={() => { logout(); setDropdownOpen(false); }}>
            Sign Out
          </button>
        </div>
      )}
    </div>
  );
};

/**
 * Country code to flag emoji
 */
const countryFlag = (code) => {
  if (!code || code.length !== 2) return '';
  return String.fromCodePoint(
    ...[...code.toUpperCase()].map(c => 0x1F1E6 + c.charCodeAt(0) - 65)
  );
};

/**
 * Table card — one open table in the lobby
 */
const TableCard = ({ table, onJoin, isOwn }) => (
  <div className={`lobby-table-card ${isOwn ? 'lobby-table-card--own' : ''}`}>
    <div className="lobby-table-card__avatar">
      {table.hostAvatarUrl ? (
        <img src={table.hostAvatarUrl} alt="" className="lobby-table-card__img" referrerPolicy="no-referrer" />
      ) : (
        <span className="lobby-table-card__initial">
          {(table.hostDisplayName || '?')[0].toUpperCase()}
        </span>
      )}
    </div>
    <div className="lobby-table-card__info">
      <div className="lobby-table-card__name">{table.hostDisplayName}</div>
      <div className="lobby-table-card__meta">
        <span className="lobby-table-card__elo">{table.hostElo}</span>
        {table.hostCountry && (
          <span className="lobby-table-card__region">
            {countryFlag(table.hostCountry)}
          </span>
        )}
      </div>
      <div className="lobby-table-card__board">
        {table.boardSize.rows}×{table.boardSize.cols}
        <span className="lobby-table-card__tc">{TIME_CONTROL_SHORT_LABELS[table.timeControl || 'live']}</span>
      </div>
    </div>
    {!isOwn && (
      <button className="lobby-table-card__join" onClick={() => onJoin(table.id)}>
        Join
      </button>
    )}
  </div>
);

/**
 * LobbyView — poker-style lobby replacing the old matchmaking queue
 */
const LobbyView = ({ onBack, onStartGame }) => {
  const { user, token, renderGoogleButton } = useAuth();
  const [tables, setTables] = useState([]);
  const [playerCount, setPlayerCount] = useState(0);
  const [queueCounts, setQueueCounts] = useState([]);
  const [myTableId, setMyTableId] = useState(null);
  const [tab, setTab] = useState('ranked'); // 'local' | 'ranked'
  const [timeControl, setTimeControl] = useState('live');
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [rows, setRows] = useState(DEFAULT_BOARD_SIZE.rows);
  const [cols, setCols] = useState(DEFAULT_BOARD_SIZE.cols);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const signInBtnRef = useRef(null);
  const heartbeatRef = useRef(null);

  // Render Google's button visibly so the personalized "Sign in as
  // <name>" preview shows up — Google ignores theme overrides for
  // that variant and always renders white. We frame it with a darker
  // padded container below so the white pill reads as intentional
  // against the menu palette rather than a jarring rectangle.
  useEffect(() => {
    if (user || !signInBtnRef.current) return;
    renderGoogleButton(signInBtnRef.current, {
      size: 'large',
      text: 'continue_with',
      shape: 'pill',
      width: 260,
    });
  }, [user, renderGoogleButton]);

  // WebSocket connection to Lobby DO
  useEffect(() => {
    if (!user || !token) return;

    const apiUrl = process.env.REACT_APP_API_URL || '';
    const wsProtocol = apiUrl.startsWith('https') ? 'wss' : 'ws';
    const wsHost = apiUrl.replace(/^https?:\/\//, '') || window.location.host;
    const wsUrl = `${wsProtocol}://${wsHost}/lobby`;

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      ws.send(JSON.stringify({ action: 'join_lobby', token }));
      setConnected(true);
    };

    ws.onmessage = (evt) => {
      try {
        const msg = JSON.parse(evt.data);
        switch (msg.type) {
          case 'lobby_state':
            setTables(msg.tables || []);
            setPlayerCount(msg.playerCount || 0);
            setQueueCounts(msg.queueCounts || []);
            break;
          case 'table_created':
            setTables(prev => [...prev, msg.table]);
            break;
          case 'table_removed':
            setTables(prev => prev.filter(t => t.id !== msg.tableId));
            if (msg.tableId === myTableId) setMyTableId(null);
            break;
          case 'matched':
            onStartGame({
              gameMode: 'online',
              gameId: msg.gameId,
              color: msg.color,
              opponent: msg.opponent,
              rows,
              cols,
              timeControl: msg.timeControl || 'live',
            });
            break;
          case 'player_count':
            setPlayerCount(msg.count);
            break;
          case 'queue_counts':
            setQueueCounts(msg.queueCounts || []);
            break;
          case 'error':
            console.warn('[Lobby] Error:', msg.message);
            break;
        }
      } catch {}
    };

    ws.onclose = () => {
      setConnected(false);
      setMyTableId(null);
    };
    ws.onerror = () => setConnected(false);

    // Heartbeat
    heartbeatRef.current = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ action: 'heartbeat' }));
      }
    }, 30_000);

    return () => {
      clearInterval(heartbeatRef.current);
      if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
        ws.close();
      }
      wsRef.current = null;
    };
  }, [user, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const sendAction = (action) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(action));
    }
  };

  const handleCreateTable = () => {
    sendAction({ action: 'create_table', boardSize: { rows, cols }, timeControl });
    // Optimistically set — will be confirmed by table_created event
    // We track it by checking tables for our userId
    setMyTableId('pending');
  };

  const handleCancelTable = () => {
    sendAction({ action: 'cancel_table' });
    setMyTableId(null);
  };

  const handleJoinTable = (tableId) => {
    sendAction({ action: 'join_table', tableId });
  };

  // Track our table from the tables list
  useEffect(() => {
    if (!user) return;
    const myTable = tables.find(t => t.hostUserId === user.id);
    if (myTable) {
      setMyTableId(myTable.id);
    } else if (myTableId && myTableId !== 'pending') {
      setMyTableId(null);
    }
  }, [tables, user]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateRows = (v) => {
    let val = v;
    if (val % 2 === 0) val += 1;
    setRows(Math.max(9, Math.min(99, val)));
  };

  const updateCols = (v) => {
    let val = v;
    if (val % 2 === 0) val += 1;
    setCols(Math.max(5, Math.min(99, val)));
  };

  // Filter tables by selected time control, then sort by active tab.
  const tablesForTimeControl = tables.filter(t => (t.timeControl || 'live') === timeControl);
  const sortedTables = [...tablesForTimeControl].sort((a, b) => {
    if (tab === 'local' && user) {
      // Geographic proximity: same region first, same country second, rest third
      const aScore =
        (a.hostCountry === user.country_code && a.hostRegion === user.region_code) ? 0 :
        (a.hostCountry === user.country_code) ? 1 : 2;
      const bScore =
        (b.hostCountry === user.country_code && b.hostRegion === user.region_code) ? 0 :
        (b.hostCountry === user.country_code) ? 1 : 2;
      return aScore - bScore || a.createdAt - b.createdAt;
    }
    // Ranked: sort by ELO proximity to viewer
    const myElo = user?.elo_rating ?? 1200;
    return Math.abs(a.hostElo - myElo) - Math.abs(b.hostElo - myElo);
  });

  // Aggregate per-time-control waiting counts across all board sizes for the pills.
  const waitingByTC = LIVE_TIME_CONTROLS.reduce((acc, tc) => {
    acc[tc] = queueCounts.filter(q => q.timeControl === tc).reduce((s, q) => s + q.count, 0);
    return acc;
  }, {});

  // Not signed in
  if (!user) {
    return (
      <div className="menu-content config-content">
        <h2 className="config-title">Play Online</h2>
        <div className="config-section">
          <p className="config-hint">Sign in to play online</p>
          <div className="config-row--centered" style={{ display: 'flex', justifyContent: 'center' }}>
            <div className="lobby-signin-frame">
              <div className="lobby-signin-frame__gsi" ref={signInBtnRef} />
            </div>
          </div>
          <div className="config-actions">
            <MenuButton text="Back" onClick={onBack} variant="secondary" />
          </div>
        </div>
      </div>
    );
  }

  if (showLeaderboard) {
    return <LeaderboardScene onBack={() => setShowLeaderboard(false)} />;
  }

  return (
    <div className="menu-content config-content" style={{ maxWidth: 700 }}>
      <h2 className="config-title">Play Online</h2>

      {/* Time control pills — selects both the visible tables and the time
          control used when creating a new table. */}
      <div className="lobby-tc-pills">
        {LIVE_TIME_CONTROLS.map(tc => (
          <button
            key={tc}
            type="button"
            className={`lobby-tc-pill ${timeControl === tc ? 'lobby-tc-pill--active' : ''}`}
            onClick={(e) => { setTimeControl(tc); e.currentTarget.blur(); }}
          >
            <span className="lobby-tc-pill__label">{TIME_CONTROL_LABELS[tc]}</span>
            <span className="lobby-tc-pill__count">{waitingByTC[tc] || 0}</span>
          </button>
        ))}
      </div>

      {/* Room Tabs */}
      <div className="lobby-tabs">
        <button
          className={`lobby-tab ${tab === 'ranked' ? 'lobby-tab--active' : ''}`}
          onClick={() => setTab('ranked')}
        >Ranked</button>
        <button
          className={`lobby-tab ${tab === 'local' ? 'lobby-tab--active' : ''}`}
          onClick={() => setTab('local')}
        >Local</button>
      </div>
      <p className="lobby-tab-desc">
        {tab === 'ranked'
          ? 'Sorted by closest ELO rating'
          : user?.region_code
            ? `Players near ${user.region_code}, ${user.country_code} shown first`
            : user?.country_code
              ? `Players in ${user.country_code} shown first`
              : 'Sorted by geographic proximity'}
      </p>

      {/* Create Table Bar */}
      {!myTableId && (
        <div className="lobby-create-bar">
          <div className="config-row config-row--centered" style={{ gap: 8 }}>
            <ArrowSelector label="Rows" value={rows} onPrev={() => updateRows(rows - 2)} onNext={() => updateRows(rows + 2)} min={9} max={99} />
            <ArrowSelector label="Cols" value={cols} onPrev={() => updateCols(cols - 2)} onNext={() => updateCols(cols + 2)} min={5} max={99} />
          </div>
          <MenuButton
            text="Create Table"
            onClick={handleCreateTable}
            variant="primary"
            disabled={!connected}
          />
        </div>
      )}

      {/* Your Table Banner */}
      {myTableId && (
        <div className="lobby-waiting-banner">
          <div className="lobby-waiting-banner__text">
            <span className="lobby-waiting-spinner" />
            Waiting for opponent...
          </div>
          <MenuButton text="Cancel" onClick={handleCancelTable} variant="secondary" />
        </div>
      )}

      {/* Table Grid */}
      {sortedTables.length === 0 ? (
        <div className="lobby-empty">
          No {TIME_CONTROL_LABELS[timeControl]} tables open. Create one!
        </div>
      ) : (
        <div className="lobby-table-grid">
          {sortedTables.map(table => (
            <TableCard
              key={table.id}
              table={table}
              onJoin={handleJoinTable}
              isOwn={table.hostUserId === user?.id}
            />
          ))}
        </div>
      )}

      {/* Footer */}
      <div className="lobby-footer">
        <span className="lobby-player-count">{playerCount} online</span>
        <div className="lobby-footer__buttons">
          <MenuButton text="Leaderboard" onClick={() => setShowLeaderboard(true)} variant="secondary" />
          <MenuButton text="Back" onClick={onBack} variant="secondary" />
        </div>
      </div>
    </div>
  );
};

/**
 * Main Menu Component with scene management
 */
const MainMenu = ({
  onStartGame,
  onStartTutorial,
  savedSession,
  onResumeGame,
  onDiscardSession,
  onRejoinOnlineGame,
  initialScene,
  initialAttemptMode = null,
  onSceneChange,
  onNavigateScene,
}) => {
  const [scene, setScene] = useState(initialScene || MENU_SCENES.MAIN);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [visitCount, setVisitCount] = useState(0);
  // Picked on PUZZLE_MODE_SELECT, consumed by PuzzleConfig (and downstream
  // by App.js via the config payload). null when not in the puzzle flow.
  // Hydrates from initialAttemptMode so deep links like /puzzles/watch
  // remember "this is the LLM-watch config" on first render.
  const [puzzleAttemptMode, setPuzzleAttemptMode] = useState(initialAttemptMode);
  const overlayRef = useRef(null);

  // Hand the URL/path back to the route shim when present; the router owns
  // the History API now, so the previous home-rolled pushState/popstate
  // dance is gone. Local setScene stays as a fallback for any unrouted
  // mount of MainMenu (tests, storybook), but the routed path is primary.
  const handleNavigate = (newScene, opts = {}) => {
    setSelectedIndex(0);
    overlayRef.current?.scrollTo(0, 0);
    onSceneChange?.(newScene);
    if (onNavigateScene) {
      onNavigateScene(newScene, opts);
    } else {
      setScene(newScene);
    }
  };

  const handleBack = useCallback(() => {
    setSelectedIndex(0);
    setVisitCount(c => c + 1);
    overlayRef.current?.scrollTo(0, 0);
    onSceneChange?.(MENU_SCENES.MAIN);
    if (onNavigateScene) {
      onNavigateScene(MENU_SCENES.MAIN);
    } else {
      setScene(MENU_SCENES.MAIN);
    }
  }, [onSceneChange, onNavigateScene]);

  const renderScene = () => {
    switch (scene) {
      case MENU_SCENES.VS_COMPUTER:
        return <VsComputerConfig onBack={handleBack} onStartGame={onStartGame} />;
      case MENU_SCENES.LOCAL_MULTIPLAYER:
        return <LocalMultiplayerConfig onBack={handleBack} onStartGame={onStartGame} />;
      case MENU_SCENES.PUZZLE_MODE_SELECT:
        return (
          <PuzzleModeSelect
            onBack={handleBack}
            onPickMode={(mode) => {
              setPuzzleAttemptMode(mode);
              // Pass attemptMode through so the route shim can pick
              // /puzzles/solve vs /puzzles/watch (both share the
              // MENU_SCENES.PUZZLE scene id).
              handleNavigate(
                mode === 'endless'
                  ? MENU_SCENES.ENDLESS_PUZZLE_CONFIG
                  : MENU_SCENES.PUZZLE,
                { attemptMode: mode }
              );
            }}
          />
        );
      case MENU_SCENES.PUZZLE:
        return (
          <PuzzleConfig
            onBack={() => handleNavigate(MENU_SCENES.PUZZLE_MODE_SELECT)}
            onStartGame={onStartGame}
            attemptMode={puzzleAttemptMode || 'manual'}
          />
        );
      case MENU_SCENES.ENDLESS_PUZZLE_CONFIG:
        return (
          <EndlessPuzzleConfig
            onBack={() => handleNavigate(MENU_SCENES.PUZZLE_MODE_SELECT)}
            onStartGame={onStartGame}
          />
        );
      case MENU_SCENES.ONLINE_MULTIPLAYER:
        return <LobbyView onBack={handleBack} onStartGame={onStartGame} />;
      case MENU_SCENES.PRICING:
        return <PricingScene onBack={handleBack} />;
      case MENU_SCENES.LEADERBOARD:
        return <LeaderboardScene onBack={handleBack} />;
      case MENU_SCENES.MAIN:
      default:
        return (
          <MainMenuContent
            key={visitCount}
            onNavigate={handleNavigate}
            onStartTutorial={onStartTutorial}
            selectedIndex={selectedIndex}
            setSelectedIndex={setSelectedIndex}
            savedSession={savedSession}
            onResumeGame={onResumeGame}
            onDiscardSession={onDiscardSession}
            onRejoinOnlineGame={onRejoinOnlineGame}
          />
        );
    }
  };

  return (
    <div className="main-menu">
      <MemoizedGraphAnimator />
      <MenuAuthWidget />
      <div className="menu-overlay" ref={overlayRef}>
        {renderScene()}
      </div>
    </div>
  );
};

export default MainMenu;